/**
 * Turns the server's PDF conversion (POST /articles/upload, pdf.service.ts)
 * into an editor draft, the same way the Rise import does (importHandoff).
 *
 * The server answers { title, text, images }: the text marks each picture
 * with [[PDF_IMAGE:<id>]] and `images` carries them as data URLs.
 *
 * Every picture becomes a File behind a placeholder, which the editor stages
 * so it is uploaded only when the article is saved.
 *
 * Vector graphics arrive as SVG. SVG is converted to PNG because the platform
 * does not accept SVG uploads.
 */

const MARKER =
  /\[\[PDF_IMAGE:([^\]]+)\]\]/g;

async function dataUrlToBlob(
  dataUrl,
) {
  const response =
    await fetch(dataUrl);

  return response.blob();
}

function svgToPng(
  blob,
  width,
  height,
) {
  return new Promise(
    (resolve, reject) => {
      const url =
        URL.createObjectURL(blob);

      const image =
        new Image();

      image.onload = () => {
        const w =
          Math.max(
            1,
            Math.round(
              width ||
                image.naturalWidth ||
                800,
            ),
          );

        const h =
          Math.max(
            1,
            Math.round(
              height ||
                image.naturalHeight ||
                600,
            ),
          );

        /*
         * Twice the size for sharper
         * rendering on high-density screens.
         */
        const canvas =
          document.createElement(
            'canvas',
          );

        canvas.width =
          w * 2;

        canvas.height =
          h * 2;

        const context =
          canvas.getContext('2d');

        if (!context) {
          URL.revokeObjectURL(url);

          reject(
            new Error(
              'Canvas-Kontext konnte nicht erstellt werden.',
            ),
          );

          return;
        }

        context.drawImage(
          image,
          0,
          0,
          canvas.width,
          canvas.height,
        );

        URL.revokeObjectURL(url);

        canvas.toBlob(
          (png) => {
            if (png) {
              resolve(png);
            } else {
              reject(
                new Error(
                  'SVG konnte nicht umgewandelt werden.',
                ),
              );
            }
          },
          'image/png',
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);

        reject(
          new Error(
            'SVG konnte nicht gelesen werden.',
          ),
        );
      };

      image.src = url;
    },
  );
}

function baseName(
  name,
  fallback,
) {
  return (
    name || fallback
  ).replace(
    /[^\w\.-]+/g,
    '-',
  );
}

export async function pdfResultToDraft(
  result,
) {
  const files = [];

  const placeholders =
    new Map();

  const warnings = [];

  for (
    const [
      index,
      image,
    ] of (
      result.images ?? []
    ).entries()
  ) {
    if (
      !image?.id ||
      !image?.dataUrl
    ) {
      continue;
    }

    try {
      /*
       * Whether this is a vector drawing is decided before the SVG -> PNG
       * conversion below. The server sends `isVector: true` with
       * `mimeType: image/svg+xml`; the MIME type alone is the fallback.
       */
      const isVector =
        image.isVector === true ||
        image.mimeType ===
          'image/svg+xml';

      let blob =
        await dataUrlToBlob(
          image.dataUrl,
        );

      let type =
        image.mimeType ||
        blob.type ||
        'image/png';

      let name =
        baseName(
          image.name,
          `pdf-bild-${index + 1}.png`,
        );

      /*
       * SVG is converted to PNG. isVector was taken above, so it is still
       * known afterwards that this was a vector icon.
       */
      if (
        type ===
        'image/svg+xml'
      ) {
        blob =
          await svgToPng(
            blob,
            Number(
              image.width,
            ),
            Number(
              image.height,
            ),
          );

        type =
          'image/png';

        name =
          name.replace(
            /\.svg$/i,
            '',
          ) + '.png';
      }

      if (
        !type.startsWith(
          'image/',
        )
      ) {
        warnings.push(
          `Bild ${image.id} übersprungen (Format ${type}).`,
        );

        continue;
      }

      const placeholder =
        `pdf-import://${index + 1}/${encodeURIComponent(name)}`;

      files.push({
        placeholder,

        file: new File(
          [blob],
          name,
          {
            type,
          },
        ),

        type: 'image',
      });

      /*
       * isVector travels with the placeholder: a vector icon is put into the
       * text with its own class (pdf-import-icon) when the placeholders are
       * replaced.
       */
      placeholders.set(
        image.id,
        {
          placeholder,

          isVector,

          width:
            Number(
              image.width,
            ),
        },
      );

    } catch (
      error
    ) {
      warnings.push(
        `Bild ${image.id} konnte nicht übernommen werden: ${error?.message || error}`,
      );
    }
  }

  /*
   * Replace [[PDF_IMAGE:<id>]]
   * with an actual <img>.
   *
   * We deliberately use HTML <img>
   * instead of Markdown ![]()
   * because these markers can occur
   * inside HTML table cells.
   */
  const text =
    (
      result.text ?? ''
    ).replace(
      MARKER,
      (
        marker,
        id,
      ) => {
        const entry =
          placeholders.get(id);

        if (!entry) {
          console.warn(
            'PDF IMAGE PLACEHOLDER NOT FOUND:',
            id,
          );

          return '';
        }

        /*
         * Vector PDF graphic.
         *
         * Important:
         * It receives a DIFFERENT class.
         */
        if (
          entry.isVector
        ) {
          return (
            `<img ` +
            `src="${entry.placeholder}" ` +
            `alt="" ` +
            `class="pdf-import-icon" ` +
            `/>`
          );
        }

        /*
         * Ordinary PDF image.
         */
        return (
          `<img ` +
          `src="${entry.placeholder}" ` +
          `alt="" ` +
          `class="pdf-import-image" ` +
          `/>`
        );
      },
    );

  return {
    title:
      result.title || '',

    summary: '',

    text,

    files,

    warnings,
  };
}